import React, { useCallback, useMemo } from 'react'

import { FieldErrors, UseFormTrigger, UseFormWatch } from 'react-hook-form'
import { UseFormSetValue } from 'react-hook-form/dist/types/form'

import { TableSortType } from 'src/components/molecules'
import { InternalAssignUserEditTableRow } from 'src/components/molecules/projects/InternalAssignUserEditTableRow'
import { InternalAssignUserSortItemType } from 'src/components/molecules/projects/InternalAssignUserTableHead'

import { useGetUsers } from 'src/hooks'
import {
  ProfileEntity,
  ProjectAssignUser,
  ProjectAssignUserEntity,
  UpsertProjectFormEntity,
} from 'src/types'
import { CompassRoleEntity } from 'src/types/entities/CompassRole'

type Props = {
  profile?: ProfileEntity
  compassRoles: CompassRoleEntity[]
  alreadyAssignedInternalUsers: ProjectAssignUserEntity[]
  displayedAssignedInternalUsers: ProjectAssignUser[]
  watch: UseFormWatch<UpsertProjectFormEntity>
  setValue: UseFormSetValue<UpsertProjectFormEntity>
  formErrors: FieldErrors<UpsertProjectFormEntity>
  trigger: UseFormTrigger<UpsertProjectFormEntity>
  sortItem: InternalAssignUserSortItemType
  sortType: TableSortType
  className?: string
}

export function InternalAssignUserEditTableBody({
  profile,
  compassRoles,
  alreadyAssignedInternalUsers,
  displayedAssignedInternalUsers,
  watch,
  setValue,
  formErrors,
  trigger,
  sortItem,
  sortType,
  className,
}: Props) {
  const { users: internalUsers } = useGetUsers()

  const getAssignedAt = useCallback(
    (userId: number) => {
      return alreadyAssignedInternalUsers.find(user => user.id === userId)?.assignedAt
    },
    [alreadyAssignedInternalUsers]
  )

  const sortedInternalUsers = useMemo(() => {
    return [...displayedAssignedInternalUsers].sort((a, b) => {
      const order = sortType === 'desc' ? 1 : -1

      const aAssignedAt = getAssignedAt(a.id)
      const bAssignedAt = getAssignedAt(b.id)

      if (!aAssignedAt) {
        return -1
      }

      if (!bAssignedAt) {
        return 1
      }

      if (sortItem === 'name') {
        return (a.name ?? '-') < (b.name ?? '-') ? order : -order
      }
      if (sortItem === 'email') {
        return (a.email ?? '-') < (b.email ?? '-') ? order : -order
      }
      if (sortItem === 'keywords') {
        const aKeywords = internalUsers.find(user => user.id === a.id)?.keywords
        const aTargetKeywords = aKeywords && aKeywords.length > 0 ? aKeywords[0] : '-'
        const bKeywords = internalUsers.find(user => user.id === b.id)?.keywords
        const bTargetKeywords = bKeywords && bKeywords.length > 0 ? bKeywords[0] : '-'
        return aTargetKeywords < bTargetKeywords ? order : -order
      }
      if (sortItem === 'customRole') {
        const aCustomRole = internalUsers.find(user => user.id === a.id)?.customRoles
        const aTargetCustomRoleId = aCustomRole && aCustomRole.length > 0 ? aCustomRole[0].id : -1
        const bCustomRole = internalUsers.find(user => user.id === b.id)?.customRoles
        const bTargetCustomRoleId = bCustomRole && bCustomRole.length > 0 ? bCustomRole[0].id : -1
        return aTargetCustomRoleId < bTargetCustomRoleId ? order : -order
      }
      if (sortItem === 'assignedAt') {
        return aAssignedAt < (bAssignedAt ?? new Date()) ? order : -order
      }
      if (sortItem === 'compassRole') {
        return (a.compassRolePriority ?? 0) < (b.compassRolePriority ?? 0) ? order : -order
      }

      return 0
    })
  }, [displayedAssignedInternalUsers, getAssignedAt, internalUsers, sortItem, sortType])

  // userIdをキーにしたエラーメッセージの配列。useMemoを利用するとformErrorsが変更されたときに再計算されないためuseMemoを使わない
  const errorMaps = (() => {
    const projectUserErrors = formErrors?.projectUsers
    // 配列であることの確認を入れないとtsのエラーが起きる
    if (!Array.isArray(projectUserErrors)) {
      return []
    }

    // projectUsersはソート前（watch("projectUsers")）のindex番号順にエラーメッセージが入っている
    return projectUserErrors
      ?.map((item, index) => {
        if (!watch('projectUsers')[index]) {
          return undefined
        }
        return { userId: watch('projectUsers')[index].id, message: item?.compassRoleId?.message }
      })
      .filter((v): v is { userId: number; message: string } => !!v)
  })()

  return (
    <div className={className}>
      <ul>
        {sortedInternalUsers.map(internalUser => (
          <li
            className="tw-centering tw-border-b tw-border-b-border-secondary last:tw-border-b-0"
            key={internalUser.id}
          >
            <InternalAssignUserEditTableRow
              profile={profile}
              userId={internalUser.id}
              compassRoles={compassRoles}
              selectedValue={internalUser.compassRoleId}
              alreadyAssignedInternalUserIds={alreadyAssignedInternalUsers.map(user => user.id)}
              errorMessage={
                errorMaps.find(errorMap => errorMap.userId === internalUser.id)?.message
              }
              watch={watch}
              setValue={setValue}
              trigger={trigger}
              assignedAt={getAssignedAt(internalUser.id)}
              className="tw-w-full"
            />
          </li>
        ))}
      </ul>
    </div>
  )
}
